<template>
  <CPanel v-show="!loading">
    <div class="row q-mb-md">
      <span class="text-h6">{{ $t('Filter Tags') }}</span>
    </div>
    <div
      class="row inline no-wrap full-width"
      v-for="(form, formIndex) in tagFiltersComputed"
      :key="`form-${formIndex}`"
    >
      <ItemExpandable
        :defaultOpened="true"
        :label="`${$t('OR')} (${$t(' Entry ')} ${formIndex + 1})`"
        group="formsGroup"
        class="full-width q-my-md"
      >
        <div class="q-pt-md q-px-xl">
          <div class="row justify-between">
            <span class="text-h8 text-grey-9 col-5">{{ $t('Tag name') }}</span>
            <span class="text-h8 text-grey-9 col-5">
              {{ $t('Tag Value') }}
              <HelpPopover placement="bottom">
                <p>
                  The Tag value is the filter tag value which can be prefixed by either "eq:"
                  (equal), "re:" (regular expressions based on https://github.com/google/re2), or
                  "!re:" (reverse "re:"). No prefix is the same as "eq:".
                </p>
                <p>
                  examples:<br />
                  {re:^ap-.*} returns all items begin with [ap-] like ap-south-1
                  <br />
                  {re:AmazonEC2|AmazonRDS} returns all AmazonEC2 or AmazonRDS lineitems.
                  {eq:ap-southeast-1} returns ap-southeast-1 <br />
                  {!re:^ap-.*} returns all items except anything begins with [ap-]
                </p>
                <p class="q-mb-sm">For more information, you can check :</p>
                <a target="_blank" href="https://github.com/google/re2">github.com/google/re2</a>
              </HelpPopover></span
            >
          </div>
        </div>
        <div class="q-py-lg q-px-xl">
          <div v-for="(entries, entryIndex) in form.data" :key="`entry-${entryIndex}`">
            <div v-if="entryIndex > 0" class="row inline justify-center no-wrap full-width">
              <hr style="width: 42%" />
              <span class="text-grey-5">AND</span>
              <hr style="width: 42%" />
            </div>
            <div class="row justify-between">
              <div style="padding-bottom: 38px" class="col-5 rounded-borders">
                <el-select
                  :loading="optionsLoading"
                  v-model="entries.key"
                  default-first-option
                  :placeholder="`${$t('ex')} : user:STAGE`"
                  class="full-width"
                  allow-create
                  :filter-method="val => handleTagKeyFiltering(val, entryIndex, formIndex)"
                  style="height: 40px"
                  filterable
                  @blur="clearFilter"
                  :options="tagNameOptions"
                >
                  <el-option
                    v-for="(item, index) in tagNameOptions"
                    :label="item"
                    :value="item"
                    :key="item + index"
                  >
                  </el-option>
                </el-select>
              </div>
              <div class="col-5 q-my-auto">
                <div class="column full-width">
                  <!-- the regex -->
                  <div v-if="entries.regex && !!tagValueRegex[entryIndex]">
                    <TagFilterRegexInput
                      :tagValue.sync="entries.value"
                      v-model="tagValueRegex[entryIndex]"
                    />
                  </div>
                  <!-- the regex -->

                  <div v-if="!entries.regex" class="row inline no-wrap full-width">
                    <el-select
                      :loading="optionsLoading"
                      v-model="entries.value"
                      default-first-option
                      :placeholder="`${$t('String Or regEx')}`"
                      class="full-width"
                      allow-create
                      style="height: 40px"
                      filterable
                    >
                      <el-option
                        v-for="(item, index) in getTagValueOptions(entries.key)"
                        :label="item"
                        :value="item"
                        :key="item + index"
                      >
                      </el-option>
                    </el-select>
                    <div>
                      <q-btn
                        @click="removePairFromEntry(formIndex, entryIndex)"
                        class="q-mx-md"
                        flat
                        round
                        icon="close"
                      />
                    </div>
                  </div>
                  <div class="row inline reverse no-wrap full-width q-pr-md">
                    <toggle
                      :label="$t('Use Regex')"
                      @input="v => handleEntryRegex(v, entryIndex)"
                      v-model="entries.regex"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <CButton
            color="grey-8"
            size="sm"
            outline
            icon="add"
            class="q-mt-lg text-gray-5"
            @click="addNewTagEntry(formIndex)"
          >
            <span>{{ $t('New Pair (and)') }}</span>
          </CButton>
        </div>
      </ItemExpandable>
      <div v-if="formIndex !== 0">
        <q-btn
          @click="handleRemoveTagForm(formIndex)"
          class="q-my-md"
          size="14px"
          flat
          round
          icon="close"
        />
      </div>
      <div v-else>
        <div style="width: 42px; display: block"></div>
      </div>
    </div>
    <div class="row full-width">
      <CButton outline ref="addTagBtnRef" color="primary" class="q-mt-lg" @click="addTagForm">
        {{ $t('Add Filter (or)') }}
      </CButton>
    </div>
  </CPanel>
</template>
<script>
import CButton from '@/components/uikit/CButton';
import CPanel from '@/components/uikit/CPanel';
import HelpPopover from '@/components/uikit/HelpPopover';
import Loading from '@/components/uikit/Loading';
import ItemExpandable from '@design-system/components/ItemExpandable';
import Toggle from '@design-system/components/Toggle.vue';
import TagFilterRegexInput from './TagFilterRegexInput.vue';

const messages = {
  en: {
    'Use Regex': 'Use Regex',
  },
  ja: {
    'Use Regex': '使用するRegex',
  },
};

export default {
  i18n: { messages },
  components: {
    CPanel,
    Loading,
    CButton,
    ItemExpandable,
    HelpPopover,
    Toggle,
    TagFilterRegexInput,
  },
  props: ['tagFilters', 'tagNameOptions', 'tagValueFilter', 'optionsLoading', 'loading'],
  data() {
    return {
      tagValueRegex: {},
    };
  },
  computed: {
    tagFiltersComputed: {
      set(val) {
        this.$emit('update:tagFilters', val);
      },
      get() {
        return this.tagFilters;
      },
    },
  },
  methods: {
    handleTagKeyFiltering(val, entryIndex, formIndex) {
      this.$emit('handleTagKeyFiltering', { val, entryIndex, formIndex });
    },
    clearFilter() {
      this.$emit('clearFilter');
    },
    getTagValueOptions(tagKey) {
      // we using SET instead of array, so the value should be unique, only
      // need to get the key
      return this.$store.getters['reportFilters/getTagValueOptions'](tagKey);
    },
    removePairFromEntry(formIndex, entryIndex) {
      this.$emit('removePairFromEntry', { formIndex, entryIndex });
    },
    addNewTagEntry(formIndex) {
      this.$emit('addNewTagEntry', formIndex);
    },
    handleRemoveTagForm(formIndex) {
      this.$emit('handleRemoveTagForm', formIndex);
    },
    addTagForm() {
      this.$emit('addTagForm');
    },
    handleEntryRegex(v, entryIndex) {
      if (v)
        this.$set(this.tagValueRegex, entryIndex, {
          type: null,
          values: [],
        });
      else this.$set(this.tagValueRegex, entryIndex, null);
    },
  },
};
</script>
